import { ReactElement, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  stockNewsAPI,
  newsAPI,
  NewsSectionListResponse,
  NewsArticleSummary,
  BookmarkItemType,
} from '../../services/api';
import {
  ArrowLeft,
  Loader2,
  Newspaper,
  BarChart3,
  TrendingUp,
  FileText,
  MessageSquare,
  Activity,
  Clock,
} from 'lucide-react';
import BookmarkButton from '../../components/BookmarkButton';
import BookmarkBatchProvider from '../../components/BookmarkBatchProvider';
import Pagination from '../../components/Pagination';

function bookmarkType(source: string): BookmarkItemType {
  if (source === 'economic') return 'news_economic';
  if (source === 'events') return 'news_events';
  return 'news_earnings';
}

const SECTION_CONFIG: Record<string, { name: string; icon: ReactElement; bgColor: string }> = {
  markets: {
    name: 'Markets',
    icon: <BarChart3 className="w-5 h-5" />,
    bgColor: 'bg-blue-500',
  },
  stocks: {
    name: 'Stocks',
    icon: <TrendingUp className="w-5 h-5" />,
    bgColor: 'bg-emerald-500',
  },
  earnings: {
    name: 'Earnings',
    icon: <Activity className="w-5 h-5" />,
    bgColor: 'bg-amber-500',
  },
  'sec-filings': {
    name: 'SEC Filings',
    icon: <FileText className="w-5 h-5" />,
    bgColor: 'bg-slate-600',
  },
  editorial: {
    name: 'Editorial',
    icon: <MessageSquare className="w-5 h-5" />,
    bgColor: 'bg-purple-500',
  },
  'trading-sessions': {
    name: 'Pre-Market & After Hours',
    icon: <Clock className="w-5 h-5" />,
    bgColor: 'bg-rose-500',
  },
};

function ArticleRow({ article }: { article: NewsArticleSummary }): ReactElement {
  const articleHref = newsAPI.getArticleHtmlUrl(article.source, article.id);
  return (
    <div className="group relative bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-gray-300 transition-all duration-200">
      <div className="flex items-start justify-between gap-4">
        <a href={articleHref} className="flex-1 min-w-0 block">
          <h3
            className="text-base font-semibold text-gray-900 group-hover:text-gray-700 transition-colors mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {article.title}
          </h3>
          {article.summary && (
            <p className="text-sm text-gray-500 mb-2 line-clamp-2">{article.summary}</p>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-500 bg-gray-100 rounded">
              {article.section}
            </span>
            {article.symbol && (
              <span className="px-1.5 py-0.5 text-[10px] font-mono font-medium text-slate-600 bg-slate-100 rounded">
                {article.symbol}
              </span>
            )}
            {article.key_stat && (
              <>
                <span className="text-gray-300">&middot;</span>
                <span className="text-xs font-mono font-medium text-emerald-600">{article.key_stat}</span>
              </>
            )}
            <span className="text-gray-300">&middot;</span>
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Clock className="w-3 h-3" />
              {article.time_ago}
            </div>
          </div>
        </a>
        <BookmarkButton
          itemType={bookmarkType(article.source)}
          itemId={String(article.id)}
          title={article.title}
          subtitle={article.symbol ?? undefined}
          url={articleHref}
          size="sm"
        />
      </div>
    </div>
  );
}

export default function StockNewsSection(): ReactElement {
  const { section } = useParams<{ section: string }>();
  const [page, setPage] = useState(0);
  const limit = 20;

  const sectionConfig = SECTION_CONFIG[section || ''] || {
    name: section || 'News',
    icon: <Newspaper className="w-5 h-5" />,
    bgColor: 'bg-gray-500',
  };

  const { data, isLoading, error } = useQuery<NewsSectionListResponse>({
    queryKey: ['stocks-news-section', section, page],
    queryFn: async () => {
      const response = await stockNewsAPI.getSectionArticles(section || '', {
        limit,
        offset: page * limit,
      });
      return response.data;
    },
    enabled: !!section,
    staleTime: 2 * 60 * 1000,
  });

  const batchItems = (data?.articles ?? []).map((a) => ({
    itemType: bookmarkType(a.source),
    itemId: String(a.id),
  }));

  return (
    <BookmarkBatchProvider items={batchItems}>
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Link
          to="/stocks"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Stocks
        </Link>

        <header className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className={`w-10 h-10 rounded-lg ${sectionConfig.bgColor} flex items-center justify-center`}>
              <div className="text-white">{sectionConfig.icon}</div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
                {data?.name || sectionConfig.name}
              </h1>
            </div>
          </div>
        </header>

        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-3" />
            <p className="text-sm text-gray-500">Loading articles...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <Newspaper className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">Failed to load articles</h3>
            <p className="text-sm text-gray-500">Please try again later</p>
          </div>
        )}

        {data && !isLoading && (
          <>
            <div className="space-y-3">
              {data.articles.map((article) => (
                <ArticleRow key={`${article.source}-${article.id}`} article={article} />
              ))}
            </div>

            {data.articles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Newspaper className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No articles yet</h3>
                <p className="text-sm text-gray-500">Check back later for new content</p>
              </div>
            )}

            <Pagination
              page={page}
              totalPages={Math.ceil(data.total_count / limit)}
              onPageChange={setPage}
              hasMore={data.has_more}
            />
          </>
        )}
      </div>
    </div>
    </BookmarkBatchProvider>
  );
}
